import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { PhoneConfigOrg } from '@wetags/phone-config';
import { useQuery } from 'react-query';
import HoldMusicAPI from '@frontend/api-hold-music';
import { ObjectTypeHoldMusic } from '@frontend/api-phone-audits';
import { scopedFilterAndSort } from '@frontend/filters';
import { useTranslation } from '@frontend/i18n';
import {
  SettingsTenantLocation,
  useHasPhoneSettingsAccess,
  usePhoneSettingsShallowStore,
} from '@frontend/phone-config';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { TextLink, useModalControl } from '@frontend/design-system';
import { PhoneSettingsScopedLocationFilterMenu } from '../../components/common/index-table';
import { SeeHistoryButton } from '../../components/common/see-history-button';
import { HoldMusicLocationList, ManageHoldMusicTray } from '../../components/hold-music/location-list';
import { LocationHoldMusic } from '../../components/hold-music/types';
import {
  DetailsHeader,
  LocationDetails,
} from '../../components/phone-system-history/history-details-tray/details-header';
import {
  HISTORY_TRAY_WIDTH,
  HistoryDetailsTray,
} from '../../components/phone-system-history/history-details-tray/history-details-tray';
import { PhoneSettingsPage } from '../../components/settings/settings-page';
import { queryKeys } from '../../query-keys';

export const HoldMusicSettings = () => {
  const { t } = useTranslation('phone');
  const { settingsTenantLocation, globalAvailableLocationIds } = usePhoneSettingsShallowStore(
    'settingsTenantLocation',
    'globalAvailableLocationIds'
  );
  const { getScopeName } = useAppScopeStore();

  const { data } = useQuery({
    queryKey: [settingsTenantLocation?.phoneTenantId, ...queryKeys.settings.listLocationHoldMusic()],
    queryFn: () => {
      return HoldMusicAPI.List({ tenantId: settingsTenantLocation?.phoneTenantId ?? '' });
    },
    select: (data) =>
      data?.locationHoldMusic?.sort((a, b) => getScopeName(a.locationId).localeCompare(getScopeName(b.locationId))),
    enabled: !!settingsTenantLocation?.phoneTenantId,
  });

  if (settingsTenantLocation && data) {
    return (
      <HoldMusicPageWithData
        availableLocations={globalAvailableLocationIds ?? []}
        tenantLocation={settingsTenantLocation}
        locationHoldMusic={data}
      />
    );
  }

  return (
    <PhoneSettingsPage
      title={t('Hold Music')}
      subtitle={t('Select the music that callers will hear when they are placed on hold.')}
      breadcrumbs={[
        { label: t('Phone'), to: '/phone/main' },
        { label: t('Hold Music'), to: '/phone/hold-music' },
      ]}
      renderContainer={false}
    >
      <div>{t('No Phone Tenant Selected')}</div>
    </PhoneSettingsPage>
  );
};

const HoldMusicPageWithData = ({
  availableLocations,
  tenantLocation,
  locationHoldMusic,
}: {
  availableLocations: string[];
  tenantLocation: SettingsTenantLocation;
  locationHoldMusic: LocationHoldMusic[];
}) => {
  const { t } = useTranslation('phone');
  const { modalProps, triggerProps } = useModalControl();
  const [filteredLocations, setFilteredLocations] = useState<string[]>([]);
  const { getScopeName, accessibleLocationData } = useAppScopeStore();

  const { associatedLocationIds } = usePhoneSettingsShallowStore('associatedLocationIds');
  const selectedLocations = (associatedLocationIds ?? []).map((id) => accessibleLocationData[id]).filter(Boolean);
  const { hasPhoneSystemHistoryAccess } = useHasPhoneSettingsAccess({ selectedLocations });

  const [phoneSystemHistoryEnabled] = PhoneConfigOrg.useWetag('phone-system-history');
  const historyTray = useModalControl();

  const isUnify = tenantLocation.locationType === 'unify';

  const resolvedLocations = filteredLocations.length > 0 ? filteredLocations : availableLocations;

  const filteredHoldMusic = useMemo(
    () =>
      scopedFilterAndSort({
        data: locationHoldMusic,
        scopeIds: availableLocations,
        filteredScopeIds: resolvedLocations,
        scopeIdAccessor: (holdMusic) => [holdMusic.locationId],
      }),
    [locationHoldMusic, availableLocations, resolvedLocations]
  );

  let trayData;

  if (resolvedLocations.length === 1) {
    trayData = {
      type: 'single' as const,
      label: getScopeName(resolvedLocations[0]),
      locationIds: resolvedLocations,
    };
  } else if (resolvedLocations.length !== availableLocations.length) {
    trayData = {
      type: 'multi' as const,
      label: t('{{count}} Locations', { count: resolvedLocations.length }),
      locationIds: resolvedLocations,
      mediaId: 'none',
    };
  } else {
    trayData = {
      type: 'multi' as const,
      label: tenantLocation.name ?? '',
      locationIds: resolvedLocations,
      mediaId: 'none',
    };
  }

  return (
    <>
      <PhoneSettingsPage
        title={t('Hold Music')}
        subtitle={t('Select the music that callers will hear when they are placed on hold.')}
        breadcrumbs={[
          { label: t('Phone'), to: '/phone/main' },
          { label: t('Hold Music'), to: '/phone/hold-music' },
        ]}
        renderContainer={false}
        action={
          phoneSystemHistoryEnabled &&
          hasPhoneSystemHistoryAccess && (
            <SeeHistoryButton
              isActive={historyTray.modalProps.show}
              onClick={historyTray.modalProps.show ? historyTray.closeModal : historyTray.openModal}
            />
          )
        }
        css={css`
          width: ${historyTray.modalProps.show ? `calc(100% - ${HISTORY_TRAY_WIDTH}px)` : '100%'};
          transition: width 0.3s ease;
        `}
      >
        <div style={{ display: 'grid', gap: availableLocations.length > 1 ? theme.spacing(2) : undefined }}>
          {availableLocations.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto auto',
                gap: theme.spacing(4),
                maxWidth: 700,
              }}
            >
              {availableLocations.length > 1 ? (
                <>
                  <PhoneSettingsScopedLocationFilterMenu
                    selectedOptions={filteredLocations}
                    onChange={setFilteredLocations}
                  />
                  <TextLink
                    size='large'
                    weight='bold'
                    css={{ width: 'fit-content', justifySelf: 'end' }}
                    {...triggerProps}
                  >
                    {resolvedLocations.length === availableLocations.length
                      ? t('Manage All Locations')
                      : t('Manage {{count}} Locations', { count: resolvedLocations.length })}
                  </TextLink>
                </>
              ) : null}
            </div>
          )}
          <HoldMusicLocationList tenantLocation={tenantLocation} locationHoldMusic={filteredHoldMusic} />
        </div>
        <ManageHoldMusicTray tenantLocation={tenantLocation} modalProps={modalProps} data={trayData} />
      </PhoneSettingsPage>

      <HistoryDetailsTray
        modalControls={historyTray}
        tenantId={tenantLocation.phoneTenantId ?? ''}
        callObjectType={ObjectTypeHoldMusic}
        HeaderComponent={
          <DetailsHeader objectType={ObjectTypeHoldMusic} onClose={historyTray.closeModal}>
            {isUnify && <LocationDetails locationNames={resolvedLocations.map((id) => getScopeName(id))} />}
          </DetailsHeader>
        }
      />
    </>
  );
};
